import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchVacationTypesAsync,
  createStudentVacationAsync,
  updateStudentVacationAsync,
  deleteStudentVacationAsync,
  computeEndDateAsync,
  patchSubscriptionEndDateAsync,
} from '../store/slices/studentsSlice';
import type { Student, StudentSubscription } from '../store/slices/studentsSlice';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import StudentVacationPeriodPicker from './StudentVacationPeriodPicker';
import AsyncSearchSelect from './AsyncSearchSelect';
import type { SelectOption } from './AsyncSearchSelect';
import Select from './Select';
import { formatDateEuropean } from '../utils/dateUtils';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface StudentOption extends SelectOption<number> {
  student: Student;
}

function normDate(d: string | null | undefined): string {
  if (!d) return '';
  return d.includes('T') ? d.split('T')[0] : d;
}

/** One subscription row in the affected-subscriptions panel */
interface SubRow {
  sub: StudentSubscription;
  /** Estimated end with the preview vacation applied (null if not yet computed or no change) */
  previewEstimate: string | null;
  /** Whether the preview has been computed for this row */
  previewComputed: boolean;
  /** Teacher's manual end date override (empty string = no override) */
  manualEnd: string;
}

export interface StudentVacationFormBodyProps {
  /** Edit mode: the vacation's owner. Create mode: preselect this student. */
  studentId?: number;
  /** Edit mode when set */
  vacationId?: number;
  /** Hide the student selector — the student is fixed by the caller (e.g. journal cell) */
  lockStudent?: boolean;
  /** Create-mode initial values (e.g. journal cell date, sick-leave type) */
  prefill?: { dateFrom?: string; dateTo?: string; vacationTypeId?: number };
  onSuccess: () => void;
  onCancel: () => void;
  onDeleted?: () => void;
  /** Edit mode: reports the loaded student's display name (page subtitle) */
  onStudentLoaded?: (name: string) => void;
}

/**
 * The student-vacation form body, shared between the /student-vacations pages
 * and modals (journal grid). Vacation rules, the affected-subscriptions preview
 * and manual end-date overrides all live here once.
 */
const StudentVacationFormBody: React.FC<StudentVacationFormBodyProps> = ({
  studentId,
  vacationId,
  lockStudent = false,
  prefill,
  onSuccess,
  onCancel,
  onDeleted,
  onStudentLoaded,
}) => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth?.token);
  const { vacationTypes } = useAppSelector((state) => state.students);

  const isCreate = !vacationId;

  // Student selector (create mode)
  const [selectedStudentOption, setSelectedStudentOption] = useState<StudentOption | null>(null);

  const [form, setForm] = useState({
    vacationTypeId: prefill?.vacationTypeId ? prefill.vacationTypeId.toString() : '',
    dateFrom: prefill?.dateFrom || '',
    dateTo: prefill?.dateTo || '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(!isCreate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Subscription rows — loaded when student is known
  const [subRows, setSubRows] = useState<SubRow[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const estimateTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const effectiveStudentId: string = isCreate
    ? (selectedStudentOption ? selectedStudentOption.value.toString() : '')
    : (studentId ? studentId.toString() : '');

  // ── Init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(fetchVacationTypesAsync());
  }, [dispatch]);

  // Default vacation type (create mode): derived during render so it applies as
  // soon as the types load, without an effect that would cascade renders.
  const effectiveVacationTypeId =
    isCreate && !form.vacationTypeId && vacationTypes.length > 0
      ? vacationTypes[0].id.toString()
      : form.vacationTypeId;

  // Pre-fill student (create mode)
  useEffect(() => {
    if (isCreate && studentId && !selectedStudentOption && token) {
      axios
        .get(`${API_BASE_URL}/students/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const s = res.data.student as Student;
          setSelectedStudentOption({
            value: s.id,
            label: `${s.lastName} ${s.firstName}${s.surname ? ' ' + s.surname : ''}`,
            student: s,
          });
        })
        .catch(() => {});
    }
  }, [isCreate, studentId, token, selectedStudentOption]);

  // Load existing vacation (edit mode)
  useEffect(() => {
    if (isCreate || !studentId || !vacationId) return;
    const fetchVacation = async () => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const vRes = await axios.get(
          `${API_BASE_URL}/students/${studentId}/vacations/${vacationId}`,
          { headers }
        );
        const v = vRes.data.vacation;
        setForm({
          vacationTypeId: v.vacationTypeId?.toString() || v.vacationType?.id?.toString() || '',
          dateFrom: v.dateFrom,
          dateTo: v.dateTo,
        });
        const sRes = await axios.get(`${API_BASE_URL}/students/${studentId}`, { headers });
        const s = sRes.data.student;
        onStudentLoaded?.(`${s.firstName} ${s.lastName}${s.surname ? ' ' + s.surname : ''}`);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'response' in err
            ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        setError(msg || 'Failed to load vacation');
      } finally {
        setLoading(false);
      }
    };
    fetchVacation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, studentId, vacationId, token]);

  // ── Step 1: load subscriptions when student is known ─────────────────────

  useEffect(() => {
    let cancelled = false;

    if (!effectiveStudentId || !token) {
      // Defer the clear so it does not run synchronously in the effect body.
      Promise.resolve().then(() => {
        if (!cancelled) setSubRows([]);
      });
      return () => {
        cancelled = true;
      };
    }

    // Kick off the fetch outside the synchronous effect body so the loading
    // setState does not trigger cascading renders.
    Promise.resolve().then(() => {
      if (cancelled) return;
      setSubsLoading(true);
      axios
        .get(`${API_BASE_URL}/students/${effectiveStudentId}/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          if (cancelled) return;
          const subs = res.data.subscriptions as StudentSubscription[];
          const today = new Date().toISOString().slice(0, 10);
          // Show subscriptions that aren't clearly in the past
          const relevant = subs.filter((s) => {
            const end = normDate(s.effectiveEndDate ?? s.endDate ?? s.estimatedEndDate);
            return !end || end >= today;
          });
          setSubRows(
            relevant.map((sub) => ({
              sub,
              previewEstimate: null,
              previewComputed: false,
              manualEnd: '',
            }))
          );
        })
        .catch(() => {
          if (!cancelled) setSubRows([]);
        })
        .finally(() => {
          if (!cancelled) setSubsLoading(false);
        });
    });

    return () => {
      cancelled = true;
    };
  }, [effectiveStudentId, token]);

  // ── Step 2: compute preview estimates when dates are complete ─────────────

  useEffect(() => {
    clearTimeout(estimateTimerRef.current);

    // Need both dates and at least one sub to estimate
    if (!form.dateFrom || !form.dateTo || subRows.length === 0) {
      // Clear any previous preview estimates. Deferred so it does not run
      // synchronously in the effect body (cascading-render rule).
      let cancelled = false;
      Promise.resolve().then(() => {
        if (cancelled) return;
        setSubRows((prev) =>
          prev.map((r) => ({ ...r, previewEstimate: null, previewComputed: false }))
        );
      });
      return () => {
        cancelled = true;
      };
    }

    estimateTimerRef.current = setTimeout(() => {
      const previewVac = { dateFrom: form.dateFrom, dateTo: form.dateTo };

      subRows.forEach((row) => {
        // Subscriptions with a manual endDate won't be auto-shifted by vacations
        if (row.sub.endDate) {
          setSubRows((prev) =>
            prev.map((r) =>
              r.sub.id === row.sub.id ? { ...r, previewEstimate: null, previewComputed: true } : r
            )
          );
          return;
        }

        dispatch(
          computeEndDateAsync({
            studentId: parseInt(effectiveStudentId, 10),
            startDate: row.sub.startDate,
            lessonsCount: row.sub.lessonsCount,
            previewStudentVacation: previewVac,
          })
        )
          .unwrap()
          .then((payload) => {
            setSubRows((prev) =>
              prev.map((r) =>
                r.sub.id === row.sub.id
                  ? { ...r, previewEstimate: payload.estimatedEndDate, previewComputed: true }
                  : r
              )
            );
          })
          .catch(() => {
            setSubRows((prev) =>
              prev.map((r) =>
                r.sub.id === row.sub.id ? { ...r, previewEstimate: null, previewComputed: true } : r
              )
            );
          });
      });
    }, 400);

    return () => clearTimeout(estimateTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.dateFrom, form.dateTo, effectiveStudentId, dispatch]);
  // Note: intentionally exclude subRows to avoid infinite loop;
  // we capture a snapshot of subRows when the dates change.

  // ── Student async select ──────────────────────────────────────────────────

  const loadStudentOptions = useCallback(
    async (search: string): Promise<StudentOption[]> => {
      if (!token) return [];
      const res = await axios.get(`${API_BASE_URL}/students`, {
        params: { search, limit: 50 },
        headers: { Authorization: `Bearer ${token}` },
      });
      return (res.data.students as Student[]).map((s) => ({
        value: s.id,
        label: `${s.lastName} ${s.firstName}${s.surname ? ' ' + s.surname : ''}`,
        student: s,
      }));
    },
    [token]
  );

  const handleStudentChange = useCallback((option: StudentOption | null) => {
    setSelectedStudentOption(option);
    setSubRows([]);
    setFormErrors((prev) => ({ ...prev, studentId: '' }));
  }, []);

  const handleManualEndChange = (subscriptionId: number, value: string) => {
    setSubRows((prev) =>
      prev.map((r) => (r.sub.id === subscriptionId ? { ...r, manualEnd: value } : r))
    );
  };

  // ── Form handlers ─────────────────────────────────────────────────────────

  const typeOptions = vacationTypes.map((t) => ({ value: t.id, label: t.name }));
  const selectedTypeOption =
    typeOptions.find((o) => o.value.toString() === effectiveVacationTypeId) ?? null;

  const handleTypeChange = (option: SelectOption<number> | null) => {
    setForm((prev) => ({ ...prev, vacationTypeId: option ? option.value.toString() : '' }));
    setFormErrors((prev) => ({ ...prev, vacationTypeId: '' }));
  };

  const handlePeriodChange = ({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) => {
    setForm((prev) => ({ ...prev, dateFrom, dateTo }));
    setFormErrors((prev) => ({ ...prev, dateFrom: '', dateTo: '' }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (isCreate && !effectiveStudentId) errors.studentId = 'Required';
    if (!effectiveVacationTypeId) errors.vacationTypeId = 'Required';
    if (!form.dateFrom) errors.dateFrom = 'Required';
    if (!form.dateTo) errors.dateTo = 'Required';
    if (form.dateFrom && form.dateTo && form.dateFrom > form.dateTo) {
      errors.dateTo = 'Must be on or after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isCreate) {
        await dispatch(
          createStudentVacationAsync({
            studentId: parseInt(effectiveStudentId, 10),
            data: {
              vacationTypeId: parseInt(effectiveVacationTypeId, 10),
              dateFrom: form.dateFrom,
              dateTo: form.dateTo,
            },
          })
        ).unwrap();
      } else if (studentId && vacationId) {
        await dispatch(
          updateStudentVacationAsync({
            studentId,
            vacationId,
            data: {
              vacationTypeId: parseInt(effectiveVacationTypeId, 10),
              dateFrom: form.dateFrom,
              dateTo: form.dateTo,
            },
          })
        ).unwrap();
      }

      // Apply any manual end date overrides
      const overrides = subRows.filter((r) => r.manualEnd.trim());
      await Promise.all(
        overrides.map((r) =>
          dispatch(
            patchSubscriptionEndDateAsync({
              studentId: parseInt(effectiveStudentId, 10),
              subscriptionId: r.sub.id,
              endDate: r.manualEnd,
            })
          ).unwrap()
        )
      );

      onSuccess();
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to save vacation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!studentId || !vacationId) return;
    setSubmitting(true);
    setError(null);
    try {
      await dispatch(
        deleteStudentVacationAsync({ studentId, vacationId })
      ).unwrap();
      (onDeleted ?? onSuccess)();
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to delete vacation');
    } finally {
      setSubmitting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isCreate && loading) return <LoadingSpinner />;

  const datesComplete = !!(form.dateFrom && form.dateTo);

  return (
    <div className="group-form">
      {error && (
        <div className="error-message" style={{ margin: '1rem 1rem 0' }}>
          {error}
          <button type="button" onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="form-section">
        <h2>Vacation Details</h2>

        {isCreate && !lockStudent && (
          <div className="form-row-compact">
            <div className="form-group">
              <label>Student *</label>
              <AsyncSearchSelect<number, StudentOption>
                value={selectedStudentOption}
                onChange={handleStudentChange}
                loadOptions={loadStudentOptions}
                placeholder="Search by name..."
                isError={!!formErrors.studentId}
                noOptionsMessage="No students found"
              />
              {formErrors.studentId && <span className="error-text">{formErrors.studentId}</span>}
            </div>
          </div>
        )}
        {isCreate && lockStudent && selectedStudentOption && (
          <div className="form-row-compact">
            <div className="form-group">
              <label>Student</label>
              <input type="text" className="form-input" value={selectedStudentOption.label} disabled />
            </div>
          </div>
        )}

        <div className="form-row-compact">
          <div className="form-group">
            <label>Vacation Type *</label>
            <Select<number, SelectOption<number>>
              value={selectedTypeOption}
              onChange={handleTypeChange}
              options={typeOptions}
              placeholder="Select type"
              isError={!!formErrors.vacationTypeId}
              isClearable={false}
              noOptionsMessage="No types"
            />
            {formErrors.vacationTypeId && (
              <span className="error-text">{formErrors.vacationTypeId}</span>
            )}
          </div>
        </div>

        <div className="form-row-compact">
          <div className="form-group">
            <StudentVacationPeriodPicker
              dateFrom={form.dateFrom}
              dateTo={form.dateTo}
              onChange={handlePeriodChange}
              errors={{ dateFrom: formErrors.dateFrom, dateTo: formErrors.dateTo }}
              disabled={submitting}
            />
          </div>
        </div>
      </div>

      {/* ── Subscriptions panel ──────────────────────────────────── */}
      {effectiveStudentId && (
        <div className="form-section">
          <h2>Subscriptions</h2>

          {subsLoading ? (
            <p style={{ color: '#718096', fontSize: '0.875rem' }}>Loading subscriptions…</p>
          ) : subRows.length === 0 ? (
            <p style={{ color: '#718096', fontSize: '0.875rem' }}>
              No active subscriptions found for this student.
            </p>
          ) : (
            <>
              <p style={{ fontSize: '0.875rem', color: '#718096', margin: '0 0 1rem' }}>
                {datesComplete
                  ? 'Subscription end dates will update automatically after saving. You can also set a manual override below.'
                  : 'Select vacation dates above to preview how subscription end dates will shift.'}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {subRows.map(({ sub, previewEstimate, previewComputed, manualEnd }) => {
                  const currentEnd = normDate(
                    sub.effectiveEndDate ?? sub.endDate ?? sub.estimatedEndDate
                  );
                  const hasManualOnSub = !!sub.endDate;

                  // Determine what changed
                  const willShift =
                    !hasManualOnSub &&
                    datesComplete &&
                    previewComputed &&
                    previewEstimate &&
                    currentEnd &&
                    previewEstimate !== currentEnd;

                  const noChange =
                    !hasManualOnSub &&
                    datesComplete &&
                    previewComputed &&
                    previewEstimate &&
                    currentEnd &&
                    previewEstimate === currentEnd;

                  return (
                    <div
                      key={sub.id}
                      style={{
                        padding: '0.875rem 1rem',
                        border: `1px solid ${willShift ? '#f6ad55' : '#e2e8f0'}`,
                        borderRadius: 'var(--radius)',
                        background: willShift ? '#fffaf0' : '#f9fafb',
                      }}
                    >
                      {/* Header */}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.75rem',
                          alignItems: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        <strong style={{ fontSize: '0.9rem', color: '#2d3748' }}>
                          {sub.subscriptionType?.name || 'Subscription'}
                        </strong>
                        <span style={{ fontSize: '0.85rem', color: '#718096' }}>
                          {sub.lessonsCount} lessons · from{' '}
                          {formatDateEuropean(sub.startDate)}
                        </span>
                      </div>

                      {/* End date info */}
                      <div style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.625rem' }}>
                        {hasManualOnSub ? (
                          <span>
                            Manual end:{' '}
                            <strong>{currentEnd ? formatDateEuropean(currentEnd) : '—'}</strong>
                            <span style={{ color: '#718096', marginLeft: '0.4rem' }}>
                              (fixed — vacation won't shift this)
                            </span>
                          </span>
                        ) : (
                          <span>
                            Current end:{' '}
                            <strong>
                              {currentEnd ? formatDateEuropean(currentEnd) : '—'}
                            </strong>

                            {datesComplete && !previewComputed && (
                              <span style={{ color: '#a0aec0', marginLeft: '0.4rem' }}>
                                computing…
                              </span>
                            )}

                            {willShift && previewEstimate && (
                              <span style={{ color: '#c05621', marginLeft: '0.4rem' }}>
                                → <strong>{formatDateEuropean(previewEstimate)}</strong>{' '}
                                after this vacation
                              </span>
                            )}

                            {noChange && (
                              <span style={{ color: '#38a169', marginLeft: '0.4rem' }}>
                                (vacation falls outside this subscription)
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Manual override */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label
                          style={{
                            fontSize: '0.8rem',
                            color: '#4a5568',
                            whiteSpace: 'nowrap',
                            minWidth: 'fit-content',
                          }}
                        >
                          Set end date manually:
                        </label>
                        <input
                          type="date"
                          value={manualEnd}
                          onChange={(e) => handleManualEndChange(sub.id, e.target.value)}
                          className="form-input"
                          style={{ maxWidth: '160px', height: '36px', fontSize: '0.85rem' }}
                          min={sub.startDate}
                        />
                        {manualEnd && (
                          <button
                            type="button"
                            onClick={() => handleManualEndChange(sub.id, '')}
                            className="btn-secondary"
                            style={{ height: '36px', padding: '0 0.75rem', fontSize: '0.8rem' }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      <div className="form-actions">
        {!isCreate && (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="btn-danger"
            disabled={submitting}
          >
            Delete
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn-success"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>

      {showDeleteConfirm && (
        <div
          style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: 'var(--radius)',
          }}
        >
          <p style={{ margin: '0 0 0.75rem', color: '#c53030' }}>
            Are you sure you want to delete this vacation?
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
              disabled={submitting}
            >
              {submitting ? 'Deleting…' : 'Yes, Delete'}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentVacationFormBody;
